import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonCard,
  IonCardContent,
  IonButton,
  IonIcon,
  IonSpinner,
  IonGrid,
  IonRow,
  IonCol,
  IonText,
} from '@ionic/react';
import { arrowBack, bookmark, locationOutline, briefcaseOutline, cashOutline } from 'ionicons/icons';
import { useHistory } from 'react-router-dom';
import TitleBar from '../components/TitleBar.tsx';
import { jobs } from '../services/api.ts';

interface SavedJobData {
  saved_job_id: number;
  saved_at: string;
  notes?: string;
  job: {
    id: number;
    job_url: string;
    site?: string;
    title: string;
    company: string;
    location?: string;
    description?: string;
    salary_min?: number;
    salary_max?: number;
    currency?: string;
    job_type?: string;
    is_remote?: boolean;
    created_at: string;
  };
}

const SavedJobs: React.FC = () => {
  const history = useHistory();
  const [savedJobs, setSavedJobs] = useState<SavedJobData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSavedJobs();
  }, []);

  const loadSavedJobs = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await jobs.getSavedJobs(1, 50);
      setSavedJobs(response);
    } catch (err: any) {
      console.error('Error loading saved jobs:', err);
      setError(err.response?.data?.detail || 'Failed to load saved jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    history.goBack();
  };

  const handleJobClick = (jobId: number) => {
    history.push(`/app/jobs/scraped/${jobId}`);
  };

  const formatSalary = (min?: number, max?: number, currency?: string) => {
    if (!min && !max) return 'Salary not specified';
    const curr = currency || 'USD';
    if (min && max) {
      return `${curr} ${min.toLocaleString()} - ${max.toLocaleString()}`;
    }
    if (min) return `${curr} ${min.toLocaleString()}+`;
    if (max) return `Up to ${curr} ${max.toLocaleString()}`;
    return 'Salary not specified';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <IonPage>
        <TitleBar title="Saved Jobs" />
        <IonContent>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
            minHeight: '200px'
          }}>
            <IonSpinner name="crescent" style={{ width: '48px', height: '48px' }} />
            <p style={{ color: 'var(--ion-color-medium)', fontSize: '16px', marginTop: '16px' }}>
              Loading saved jobs...
            </p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <TitleBar title="Saved Jobs" />
      <IonContent style={{ '--padding-bottom': '0px', '--padding-top': '0px' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '16px 16px 0 16px' }}>
          {/* Back button */}
          <IonButton
            fill="clear"
            onClick={handleGoBack}
            style={{ marginBottom: '12px', marginTop: '0' }}
          >
            <IonIcon icon={arrowBack} slot="start" />
            Back to Jobs
          </IonButton>

          {/* Header */}
          <div style={{ marginBottom: '16px' }}>
            <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '4px', marginTop: '0' }}>
              Saved Jobs
            </h1>
            <p style={{ color: 'var(--ion-color-medium)', fontSize: '16px', margin: '0' }}>
              {savedJobs.length} {savedJobs.length === 1 ? 'job' : 'jobs'} saved
            </p>
          </div>

          {/* Error state */}
          {error && (
            <IonCard style={{ '--background': '#fff5f5', border: '1px solid #feb2b2' }}>
              <IonCardContent style={{ padding: '20px', textAlign: 'center' }}>
                <p style={{ margin: 0, color: '#c53030', fontSize: '14px' }}>{error}</p>
                <IonButton onClick={loadSavedJobs} style={{ marginTop: '12px' }}>
                  Try Again
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {/* Empty state */}
          {!error && savedJobs.length === 0 && (
            <IonCard>
              <IonCardContent style={{ padding: '48px', textAlign: 'center' }}>
                <IonIcon
                  icon={bookmark}
                  style={{ fontSize: '64px', color: 'var(--ion-color-medium)', marginBottom: '16px' }}
                />
                <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px' }}>
                  No saved jobs yet
                </h2>
                <p style={{ color: 'var(--ion-color-medium)', fontSize: '16px', marginBottom: '24px' }}>
                  Start saving jobs to quickly access them later
                </p>
                <IonButton onClick={handleGoBack}>
                  Browse Jobs
                </IonButton>
              </IonCardContent>
            </IonCard>
          )}

          {/* Job list */}
          <IonGrid style={{ padding: '0', margin: '0' }}>
            <IonRow>
              {savedJobs.map((savedJob) => {
                const job = savedJob.job;
                return (
                  <IonCol key={savedJob.saved_job_id} size="12" sizeMd="6" sizeLg="4">
                    <IonCard
                      button
                      onClick={() => handleJobClick(job.id)}
                      style={{
                        cursor: 'pointer',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      <IonCardContent style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        {/* Header */}
                        <div style={{ marginBottom: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                            <h3 style={{ fontSize: '18px', fontWeight: '700', margin: 0, flex: 1 }}>
                              {job.title}
                            </h3>
                            <IonIcon
                              icon={bookmark}
                              style={{ fontSize: '20px', color: 'var(--ion-color-success)', marginLeft: '8px' }}
                            />
                          </div>
                          <p style={{ fontSize: '16px', fontWeight: '600', color: '#667eea', margin: '4px 0' }}>
                            {job.company}
                          </p>
                        </div>

                        {/* Job details */}
                        <div style={{ flex: 1, marginBottom: '16px' }}>
                          {job.location && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <IonIcon icon={locationOutline} style={{ fontSize: '16px', color: 'var(--ion-color-medium)' }} />
                              <IonText style={{ fontSize: '14px', color: 'var(--ion-color-dark)' }}>
                                {job.location}
                              </IonText>
                            </div>
                          )}

                          {job.job_type && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <IonIcon icon={briefcaseOutline} style={{ fontSize: '16px', color: 'var(--ion-color-medium)' }} />
                              <IonText style={{ fontSize: '14px', color: 'var(--ion-color-dark)' }}>
                                {job.job_type}
                                {job.is_remote && ' • Remote'}
                              </IonText>
                            </div>
                          )}

                          {(job.salary_min || job.salary_max) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <IonIcon icon={cashOutline} style={{ fontSize: '16px', color: 'var(--ion-color-medium)' }} />
                              <IonText style={{ fontSize: '14px', color: 'var(--ion-color-dark)' }}>
                                {formatSalary(job.salary_min, job.salary_max, job.currency)}
                              </IonText>
                            </div>
                          )}

                          {job.description && (
                            <p style={{
                              fontSize: '14px',
                              color: 'var(--ion-color-medium)',
                              margin: '12px 0 0 0',
                              lineHeight: '1.5'
                            }}>
                              {job.description}
                            </p>
                          )}
                        </div>

                        {/* Footer */}
                        <div style={{
                          fontSize: '12px',
                          color: 'var(--ion-color-medium)',
                          paddingTop: '12px',
                          borderTop: '1px solid var(--ion-color-light)'
                        }}>
                          Saved {formatDate(savedJob.saved_at)}
                        </div>
                      </IonCardContent>
                    </IonCard>
                  </IonCol>
                );
              })}
            </IonRow>
          </IonGrid>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default SavedJobs;
